import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router';
import type { DialogType } from '../../../@types';
import { PARTNER_ROLE_TYPES } from '../../../config/crm';
import { USER_ROLES } from '../../../constants/keycloak';
import { useModal } from '../../../contexts/ModalContext';
import { useMarketplace } from '../../../hooks/useMarketplace';
import type { AdditionalQueryParams } from '../../../hooks/usePagination';
import usePermissions from '../../../hooks/usePermissions';
import FilterDialogLayout from '../../layouts/FilterDialogLayout';
import { CreatedAtFilter, StatusFilter, StringArrayFilter } from './CRMFilterFields';
import { partnerFilterParams, type PartnerFilterData } from './partnerFilterParams';

type Props = DialogType & {
  changeQuery: (data: AdditionalQueryParams) => void;
};

const FilterPartners = ({ changeQuery }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const { closeModal } = useModal();
  const { role } = usePermissions();
  const { hasAffiliateSystem } = useMarketplace();

  const { control, handleSubmit } = useForm<PartnerFilterData>({
    defaultValues: partnerFilterParams.parse(searchParams),
  });

  const availableRoles = hasAffiliateSystem
    ? PARTNER_ROLE_TYPES
    : PARTNER_ROLE_TYPES.filter((roleType) => roleType !== USER_ROLES.AFFILIATE);

  const roleOptions = role === USER_ROLES.MANAGER ? [USER_ROLES.AGENT] : availableRoles;

  const applyFilters = (data: PartnerFilterData) => {
    setSearchParams(partnerFilterParams.write(searchParams, data));
    changeQuery({});
    closeModal();
  };

  const resetFilters = () => {
    setSearchParams(partnerFilterParams.write(searchParams, partnerFilterParams.parse(new URLSearchParams())));
    changeQuery({});
    closeModal();
  };

  return (
    <FilterDialogLayout
      label='partners'
      onClose={closeModal}
      onSave={handleSubmit(applyFilters)}
      onReset={resetFilters}
    >
      <StatusFilter control={control} name='isActive' />
      <StringArrayFilter control={control} name='fullName' label='Full Name' freeSolo />
      <StringArrayFilter control={control} name='email' label='Email' freeSolo />
      <CreatedAtFilter control={control} fromName='createdAt.from' toName='createdAt.to' />
      <StringArrayFilter control={control} name='roleTypes' label='Role' options={roleOptions} />
    </FilterDialogLayout>
  );
};

export default FilterPartners;
